import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormMode } from '../contexts/FormModeContext';
import { FileService, FileServiceError } from '../services/fileService';
import {
  DEFAULT_ACCEPTED_TYPES,
  DEFAULT_MAX_FILE_SIZE,
  FileResponseDto,
  FileUploadOptions,
  FileUploadProgress,
  SectionType,
} from '../types';
import styles from './FileUpload.module.css';

interface FileUploadProps {
  label: string;
  description?: string;
  sectionType: SectionType;
  options?: Partial<FileUploadOptions>;
  value?: string[]; // Array of file IDs from form
  onChange?: (fileIds: string[]) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  // Modo compacto: cuando no hay archivo, muestra una franja delgada de una
  // fila ("📎 Adjuntar archivo") en vez del dropzone grande. Opt-in (default
  // false) → no altera los usos existentes.
  compact?: boolean;
  // Resalta el dropzone en amarillo (paleta warning) cuando el archivo es
  // obligatorio y aún falta, para que se note al golpe de vista contra la UI
  // gris/azul. Opt-in (default false). El color reemplaza al texto de aviso;
  // se conserva un aria-label para no señalar solo por color (WCAG 1.4.1).
  missing?: boolean;
  templateFile?: {
    url: string;
    filename: string;
    label?: string;
  };
  templateFiles?: Array<{
    url: string;
    filename: string;
    label?: string;
  }>;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  description,
  sectionType,
  options = {},
  templateFile,
  templateFiles,
  value = [],
  onChange,
  error,
  disabled = false,
  required = false,
  compact = false,
  missing = false,
}) => {
  const { mode, applicationId } = useFormMode();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const previousFilesRef = useRef<FileResponseDto[]>([]);
  
  // Keep ref current
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  
  // Component state
  const [uploadProgress, setUploadProgress] = useState<Record<string, FileUploadProgress>>({});
  const [uploadedFiles, setUploadedFiles] = useState<FileResponseDto[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Array<{ file: File; error: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Memoize value array to prevent unnecessary re-renders
  const memoizedValue = useMemo(() => {
    if (!value || value.length === 0) return [];
    return [...value];
  }, [value]);
  
  // Merge options with defaults
  const uploadOptions: FileUploadOptions = useMemo(() => ({
    acceptedTypes: DEFAULT_ACCEPTED_TYPES,
    maxFileSize: DEFAULT_MAX_FILE_SIZE,
    maxFiles: 10,
    allowMultiple: true,
    sectionType,
    applicationId,
    ...options,
  }), [sectionType, applicationId, options]);

  // Detect if we're in admin/read-only mode (when we have an applicationId starting with 'app-')
  const isAdminMode = useMemo(() => {
    return applicationId?.startsWith('app-') || false;
  }, [applicationId]);

  // Load existing files when component mounts or value changes
  useEffect(() => {
    let isMounted = true;
    
    const loadExistingFiles = async () => {
      if (!applicationId) {
        if (isMounted) {
          setUploadedFiles([]);
        }
        return;
      }

      // Skip loading if we already have files loaded and memoizedValue hasn't really changed
      const currentFiles = previousFilesRef.current;
      if (currentFiles.length > 0 && memoizedValue.length === currentFiles.length) {
        const allFilesMatch = memoizedValue.every(id => currentFiles.some(file => file.id === id));
        if (allFilesMatch) {
          return; // No need to reload
        }
      }

      try {
        if (isMounted) {
          setIsLoading(true);
        }
        
        const allFiles = await FileService.listApplicationFiles(applicationId);
          
        if (!isMounted) return;
          
        // Filter files by section type
        const sectionFiles = allFiles.filter(file => file.sectionType === sectionType);
        
        // In admin mode, don't filter by memoizedValue since we want to show all files for the section
        const finalFiles = isAdminMode ? sectionFiles : sectionFiles.filter(file => memoizedValue.includes(file.id));
        
        // In normal mode, check if some file IDs from value don't exist in the backend
        if (!isAdminMode) {
          const existingFileIds = sectionFiles.map(file => file.id);
          const missingFileIds = memoizedValue.filter(id => !existingFileIds.includes(id));
          
          // If we have stale file IDs, clean them up
          if (missingFileIds.length > 0 && onChangeRef.current) {
            const cleanedFileIds = memoizedValue.filter(id => existingFileIds.includes(id));
            onChangeRef.current(cleanedFileIds);
          }
        }
        
        if (isMounted) {
          setUploadedFiles(finalFiles);
          previousFilesRef.current = finalFiles;
        }
      } catch (error) {
        console.error('Failed to load existing files:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadExistingFiles();
    
    return () => {
      isMounted = false;
    };
  }, [applicationId, sectionType, memoizedValue, isAdminMode]);

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragActive(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Handle file selection (both drag-drop and file input)
  const handleFileSelection = useCallback(async (files: File[]) => {
    if (!applicationId) {
      console.warn('No application ID available for file upload');
      return;
    }

    // Validate files
    const validation = FileService.validateMultipleFiles(
      files,
      uploadOptions.acceptedTypes,
      uploadOptions.maxFileSize,
      uploadOptions.maxFiles ? uploadOptions.maxFiles - uploadedFiles.length : undefined
    );

    if (!validation.valid) {
      setValidationErrors(validation.errors);
      return;
    }

    setValidationErrors([]);

    // Initialize progress tracking
    const progressMap: Record<string, FileUploadProgress> = {};
    files.forEach((file, index) => {
      const key = `${file.name}-${Date.now()}-${index}`;
      progressMap[key] = {
        file,
        progress: 0,
        status: 'uploading',
      };
    });
    setUploadProgress(progressMap);

    // Upload files sequentially
    const uploadedFileIds: string[] = [];
    for (const [key, progressItem] of Object.entries(progressMap)) {
      try {
        const response = await FileService.uploadFile(
          progressItem.file,
          applicationId,
          sectionType,
          (progress) => {
            setUploadProgress(prev => ({
              ...prev,
              [key]: { ...prev[key], progress },
            }));
          }
        );

        // Remove from progress (file will appear in uploaded files list)
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[key];
          return newProgress;
        });

        uploadedFileIds.push(response.id);
        
        // Add to uploaded files list
        setUploadedFiles(prev => [...prev, response]);

      } catch (error) {
        const errorMessage = error instanceof FileServiceError 
          ? error.message 
          : 'Error desconocido durante la carga';

        setUploadProgress(prev => ({
          ...prev,
          [key]: {
            ...prev[key],
            status: 'failed',
            error: errorMessage,
          },
        }));
      }
    }

    // Update form value with new file IDs
    if (onChange && uploadedFileIds.length > 0) {
      const newValue = [...value, ...uploadedFileIds];
      onChange(newValue);
    }

    // Clear progress after a delay
    setTimeout(() => {
      setUploadProgress({});
    }, 3000);

  }, [applicationId, sectionType, uploadOptions, uploadedFiles.length, value, onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    handleFileSelection(files);
  }, [disabled, handleFileSelection]);

  // Handle file input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFileSelection(files);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileSelection]);

  // Handle file deletion
  const handleDeleteFile = useCallback(async (fileId: string) => {
    try {
      await FileService.deleteFile(fileId);
      
      // Remove from uploaded files
      setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
      
      // Update form value
      if (onChange) {
        const newValue = value.filter(id => id !== fileId);
        onChange(newValue);
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
      // Could add toast notification here
    }
  }, [value, onChange]);

  // Handle file download
  const handleDownloadFile = useCallback((file: FileResponseDto) => {
      FileService.downloadFile(file.id, file.originalName);
  }, []);

  // Handle retry upload
  const handleRetryUpload = useCallback((progressKey: string) => {
    const progressItem = uploadProgress[progressKey];
    if (progressItem) {
      handleFileSelection([progressItem.file]);
    }
  }, [uploadProgress, handleFileSelection]);

  // Click handler for dropzone
  const handleDropzoneClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  // Format accepted types for display
  const getAcceptedTypesText = useCallback(() => {
    if (!uploadOptions.acceptedTypes) return '';
    
    const extensions = uploadOptions.acceptedTypes
      .filter(type => !type.includes('/'))
      .map(ext => ext.replace('.', '').toUpperCase())
      .join(', ');
    
    return extensions || 'PDF, DOC, DOCX, XLS, XLSX, JPG, PNG';
  }, [uploadOptions.acceptedTypes]);

  return (
    <div
      className={`${styles.fileUploadContainer} ${compact ? styles.fileUploadContainerCompact : ''}`}
    >
      {label && <label className={styles.label}>
        {label} {required && '*'}
      </label>}
      
      {description && (
        <div className={styles.fieldDescription}>
          {description}
          {templateFile && (
            <>
              {' '}
              <a
                href={templateFile.url}
                download={templateFile.filename}
                className={styles.templateLink}
              >
                {templateFile.label || 'Descargar plantilla'}
              </a>
            </>
          )}
          {templateFiles && templateFiles.length > 0 && (
            <>
              {' '}
              {templateFiles.map((template, index) => (
                <span key={index}>
                  <a
                    href={template.url}
                    download={template.filename}
                    className={styles.templateLink}
                  >
                    {template.label || 'Descargar plantilla'}
                  </a>
                  {index < templateFiles.length - 1 && ' | '}
                </span>
              ))}
            </>
          )}
        </div>
      )}

      {/* Read-only mode: Show status when disabled */}
      {disabled && !isLoading && Object.keys(uploadProgress).length === 0 
        && uploadedFiles.length === 0 && (
        <div className={styles.readOnlyStatus}>
          <div className={styles.readOnlyMessage}>
            <div className={styles.readOnlyIcon}>📄</div>
            <div>
              <strong>Sin archivos cargados</strong>
              <div className={styles.readOnlySubtext}>
                No se han subido archivos para esta sección
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drag and drop zone - only show if not disabled and max files not reached */}
      {!disabled && (!uploadOptions.maxFiles || uploadedFiles.length < uploadOptions.maxFiles) && Object.keys(uploadProgress).length === 0 && (
        compact ? (
          // Franja delgada de una fila (modo compacto): mismo comportamiento
          // drag/click, menos altura.
          <div
            className={`${styles.dropzoneCompact} ${missing ? styles.dropzoneCompactWarning : ''} ${dragActive ? styles.dragActive : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleDropzoneClick}
            role="button"
            tabIndex={0}
            aria-label={missing ? 'Falta adjuntar archivo (obligatorio)' : undefined}
          >
            <span className={styles.dropzoneCompactIcon}>📎</span>
            <span className={styles.dropzoneCompactText}>
              {mode === 'static'
                ? 'Carga disponible solo en modo dinámico'
                : dragActive
                  ? 'Suelta el archivo aquí'
                  : 'Adjuntar archivo'}
            </span>
            <span className={styles.dropzoneCompactHint}>
              {getAcceptedTypesText()}
            </span>
          </div>
        ) : (
        <div
          className={`${styles.dropzone} ${missing ? styles.dropzoneWarning : ''} ${dragActive ? styles.dragActive : ''}`}
          onDragEnter={handleDragEnter}
          aria-label={missing ? 'Falta adjuntar archivo (obligatorio)' : undefined}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleDropzoneClick}
        >
        <div className={styles.dropzoneContent}>
          <div className={styles.uploadIcon}>📎</div>
          <div className={styles.dropzoneText}>
            {mode === 'static'
              ? 'Funcionalidad de carga disponible solo en modo dinámico'
              : dragActive
                ? 'Suelta los archivos aquí'
                : 'Arrastra archivos aquí o haz clic para seleccionar'
            }
          </div>
          {mode === 'dynamic' && (
            <>
              <div className={styles.dropzoneSubtext}>
                Tipos permitidos: {getAcceptedTypesText()}
                <br />
                Tamaño máximo: {FileService.formatFileSize(uploadOptions.maxFileSize || DEFAULT_MAX_FILE_SIZE)}
                {uploadOptions.maxFiles && ` • Máximo ${uploadOptions.maxFiles} archivos`}
              </div>
              <button
                type="button"
                className={styles.browseButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDropzoneClick();
                }}
              >
                Seleccionar archivos
              </button>
            </>
          )}
        </div>
        </div>
        )
      )}

      {/* Max files reached message */}
      {uploadOptions.maxFiles && uploadOptions.maxFiles > 1 && uploadedFiles.length >= uploadOptions.maxFiles && (
        <div className={styles.maxFilesReached}>
          Máximo de {uploadOptions.maxFiles} archivo {uploadOptions.maxFiles > 1 ? 's' : ''} alcanzado
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className={styles.fileInput}
        multiple={uploadOptions.allowMultiple}
        accept={uploadOptions.acceptedTypes?.join(',')}
        onChange={handleFileInputChange}
        disabled={disabled || mode === 'static'}
      />

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className={styles.errorList}>
          <div className={styles.errorListTitle}>Errores de validación:</div>
          {validationErrors.map((error, index) => (
            <div key={index} className={styles.errorItem}>
              <strong>{error.file.name}:</strong> {error.error}
            </div>
          ))}
        </div>
      )}

      {/* Upload progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className={styles.fileList}>
          <div className={styles.fileListHeader}>Cargando archivos...</div>
          {Object.entries(uploadProgress).map(([key, progress]) => (
            <div key={key} className={`${styles.fileItem} ${styles[progress.status]}`}>
              <div className={styles.fileInfo}>
                <div className={styles.fileIcon}>
                  {FileService.getFileIcon(progress.file.type)}
                </div>
                <div className={styles.fileDetails}>
                  <div className={styles.fileName}>{progress.file.name}</div>
                  <div className={styles.fileMetadata}>
                    <span>{FileService.formatFileSize(progress.file.size)}</span>
                    <span>{FileService.getFileTypeLabel(progress.file.type)}</span>
                  </div>
                  {progress.status === 'uploading' && (
                    <div className={styles.progressContainer}>
                      <div className={styles.progressBar}>
                        <div 
                          className={styles.progressFill} 
                          style={{ width: `${progress.progress}%` }}
                        />
                      </div>
                      <div className={styles.progressText}>
                        {progress.progress}%
                      </div>
                    </div>
                  )}
                  {progress.error && (
                    <div className={styles.errorMessage}>
                      {progress.error}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.fileActions}>
                <div className={`${styles.statusIndicator} ${styles[progress.status]}`}>
                  {progress.status === 'uploading' && (
                    <>
                      <div className={styles.spinner} />
                      Cargando...
                    </>
                  )}
                  {progress.status === 'completed' && '✓ Completado'}
                  {progress.status === 'failed' && (
                    <>
                      ✗ Error
                      <button
                        type="button"
                        className={`${styles.actionButton} ${styles.retryButton}`}
                        onClick={() => handleRetryUpload(key)}
                      >
                        ↻ Reintentar
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded files list */}
      {(isLoading || uploadedFiles.length > 0) && (
        <div className={styles.fileList}>
          {/* En modo compacto el encabezado sobra: el contexto ya rotula el
              archivo (evita redundancia "Archivo / Archivos cargados"). */}
          {uploadedFiles.length > 0 && !compact && (
            <div className={styles.fileListHeader}>
              Archivos cargados ({uploadedFiles.length})
            </div>
          )}
          
          {isLoading && uploadedFiles.length === 0  && (
            <div className={styles.loading}>
              <div className={styles.spinner} />
              Cargando archivos existentes...
            </div>
          )}
          
          {uploadedFiles.map((file) => (
            <div key={file.id} className={`${styles.fileItem} ${styles.completed}`}>
              <div className={styles.fileInfo}>
                <div className={styles.fileIcon}>
                  {FileService.getFileIcon(file.mimeType)}
                </div>
                <div className={styles.fileDetails}>
                  <div className={styles.fileName}>{file.originalName}</div>
                  <div className={styles.fileMetadata}>
                    <span>{FileService.formatFileSize(file.size)}</span>
                    <span>{FileService.getFileTypeLabel(file.mimeType)}</span>
                    <span>Cargado: {new Date(file.uploadedAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div className={styles.fileActions}>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.downloadButton}`}
                  onClick={() => handleDownloadFile(file)}
                  title="Descargar archivo"
                >
                  ⬇ Descargar
                </button>
                {mode === 'dynamic' && !disabled && (
                  <button
                    type="button"
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                    onClick={() => handleDeleteFile(file.id)}
                    title="Eliminar archivo"
                    aria-label="Eliminar archivo"
                  >
                    <i className="uc-icon icon-size--sm">delete</i>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form validation error */}
      {error && (
        <span className={styles.errorMessage}>
          {error}
        </span>
      )}
    </div>
  );
};

export default FileUpload;